import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Clock, 
  User, 
  Car, 
  Loader2, 
  CheckCircle,
  Phone,
  Mail,
  ExternalLink,
  LogOut,
  Archive,
  ChevronDown,
  ChevronUp,
  AlertTriangle
} from 'lucide-react';
import OpzionatoBadge from '../components/OpzionatoBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WaitingRoomPage = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const [archivedOffers, setArchivedOffers] = useState([]);
  const [showArchive, setShowArchive] = useState(false);

  const fetchOffers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cliente/offerte`, { headers: getAuthHeader() });
      setOffers(response.data.offers || []);
      setArchivedOffers(response.data.archived_offers || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
      // Non mostrare errore se non ci sono offerte
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleDownloadPDF = async (offerId) => {
    setDownloading(offerId);
    try {
      const response = await axios.get(`${API}/offers/${offerId}/pdf`, {
        responseType: 'blob',
        headers: getAuthHeader()
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Preventivo_${offerId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF scaricato con successo');
      // Ricarica per aggiornare lo stato
      fetchOffers();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Errore nel download del PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Ma che noleggio" className="h-10 object-contain" />
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Esci
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Banner */}
        <Card className="mb-8 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-7 h-7 text-amber-600" />
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-slate-800 mb-2">
                  Benvenuto/a, {user?.name}!
                </h1>
                <p className="text-slate-600 mb-3">
                  Il tuo profilo è in fase di verifica. Un nostro consulente ti contatterà a breve 
                  per completare l&apos;attivazione.
                </p>
                <Badge className="bg-amber-500 text-white">
                  <Clock className="w-3 h-3 mr-1" />
                  In Attesa di Attivazione
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              I tuoi dati
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4" />
                <span>{user?.email}</span>
              </div>
              {user?.telefono && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4" />
                  <span>{user?.telefono}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Offers Section */}
        <Card data-testid="waiting-room-offers">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              I tuoi Preventivi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {offers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nessun preventivo disponibile al momento.</p>
                <p className="text-sm mt-2">Verrai contattato dal nostro team per preparare il tuo preventivo personalizzato.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {offers.map((offer) => {
                  // Check if vehicle is opzionato
                  const isOpzionato = offer.vehicle_info?.opzionato_ae || offer.vehicle_info?.opzionato_af;
                  
                  return (
                  <div 
                    key={offer.id}
                    className={`p-4 border rounded-lg transition-colors ${isOpzionato ? 'border-amber-400 bg-amber-50' : 'bg-slate-50 hover:bg-slate-100'}`}
                    data-testid={`offer-${offer.id}`}
                  >
                    {/* Avviso OPZIONATO - Prominente in cima */}
                    {isOpzionato && (
                      <div className="mb-4">
                        <OpzionatoBadge 
                          opzionato_ae={offer.vehicle_info?.opzionato_ae}
                          opzionato_af={offer.vehicle_info?.opzionato_af}
                          variant="inline"
                        />
                      </div>
                    )}
                    
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        {offer.vehicle_info?.immagine ? (
                          <img 
                            src={offer.vehicle_info.immagine} 
                            alt={offer.vehicle_info?.modello}
                            className="w-20 h-14 object-cover rounded"
                          />
                        ) : (
                          <div className="w-20 h-14 bg-slate-200 rounded flex items-center justify-center">
                            <Car className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-slate-800">
                            {offer.vehicle_info?.marca} {offer.vehicle_info?.modello}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {offer.vehicle_info?.versione}
                          </p>
                          <p className="text-lg font-bold text-primary mt-1">
                            €{offer.canone_finale?.toLocaleString('it-IT')}/mese
                          </p>
                          <p className="text-xs text-slate-400">
                            {offer.durata_mesi} mesi • {offer.km_annui?.toLocaleString('it-IT')} km/anno
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        {offer.pdf_downloaded && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            PDF già scaricato
                          </Badge>
                        )}
                        <Button
                          onClick={() => handleDownloadPDF(offer.id)}
                          disabled={downloading === offer.id}
                          className="bg-primary hover:bg-primary/90"
                          data-testid={`download-pdf-${offer.id}`}
                        >
                          {downloading === offer.id ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scaricamento...</>
                          ) : (
                            <><Download className="w-4 h-4 mr-2" />Scarica Preventivo PDF</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Archivio Offerte Vecchie (>6 mesi) */}
        {archivedOffers.length > 0 && (
          <Card className="border-slate-300">
            <CardHeader 
              className="cursor-pointer bg-slate-50"
              onClick={() => setShowArchive(!showArchive)}
            >
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Archive className="w-5 h-5 text-slate-500" />
                  Archivio Preventivi
                  <span className="text-sm font-normal text-slate-500">({archivedOffers.length})</span>
                </div>
                {showArchive ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </CardTitle>
            </CardHeader>
            {showArchive && (
              <CardContent className="pt-4">
                <p className="text-sm text-slate-500 mb-4">
                  Preventivi precedenti (più di 6 mesi fa)
                </p>
                <div className="space-y-3">
                  {archivedOffers.map((offer) => (
                    <div 
                      key={offer.id}
                      className="p-4 border rounded-lg bg-slate-50 opacity-75 hover:opacity-100 transition-opacity"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="w-16 h-12 bg-slate-200 rounded flex items-center justify-center">
                            <Car className="w-6 h-6 text-slate-400" />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-700">
                              {offer.vehicle_info?.marca} {offer.vehicle_info?.modello}
                            </h3>
                            <p className="text-sm text-slate-500">
                              €{offer.canone_finale?.toLocaleString('it-IT')}/mese • {offer.durata_mesi} mesi
                            </p>
                            <p className="text-xs text-slate-400">
                              Creato il {new Date(offer.created_at).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPDF(offer.id)}
                          disabled={downloading === offer.id}
                        >
                          {downloading === offer.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><Download className="w-4 h-4 mr-1" />PDF</>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2">Cosa succede ora?</h3>
          <ul className="space-y-2 text-sm text-blue-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-500" />
              <span>Un nostro consulente verificherà il tuo profilo</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-500" />
              <span>Riceverai un&apos;email quando il tuo account sarà attivato</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-500" />
              <span>Potrai accedere al catalogo completo e personalizzare i preventivi</span>
            </li>
          </ul>
        </div>

        {/* Contact Support */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>Hai domande? Contattaci a <a href="mailto:preventivi@machenoleggio.it" className="text-primary hover:underline">preventivi@machenoleggio.it</a></p>
        </div>
      </main>
    </div>
  );
};

export default WaitingRoomPage;
